import { msg } from "@lingui/core/macro";
import { ErrorBoundary, type JSX } from "solid-js";
import { AlertTriangle, RefreshCw, RotateCcw } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { cn } from "@bodega/shared";

interface ErrorFallbackProps {
  error: unknown;
  reset: () => void;
  /** Compact variant for inline data regions (no full-page centering). */
  inline?: boolean;
}

/**
 * Premium, theme-aware error fallback. Renders a decorative SVG illustration
 * (uses `currentColor` from the destructive token, purely presentational and
 * `aria-hidden`), a clear title + description, and two actions: "Retry"
 * (resets the boundary so the subtree re-renders) and "Reload" (hard reload).
 * Motion is suppressed under `prefers-reduced-motion` via the global guard.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
function ErrorFallback(props: ErrorFallbackProps) {
  const { t } = useI18n();

  const message = () =>
    props.error instanceof Error ? props.error.message : t(msg`An unexpected error has occurred.`);

  return (
    <div
      role="alert"
      class={cn(
        "flex w-full flex-col items-center justify-center p-6 text-center",
        props.inline ? "flex-1 min-h-[240px]" : "min-h-dvh",
      )}
    >
      <GlassSurface class="flex w-full max-w-md flex-col items-center gap-6 p-8">
        {/* Decorative illustration — theme-aware via currentColor (destructive). */}
        <div class="relative">
          <div
            aria-hidden="true"
            class="pointer-events-none absolute inset-0 -z-10 rounded-full bg-destructive/15 blur-2xl"
          />
          <svg
            aria-hidden="true"
            viewBox="0 0 120 120"
            class="pointer-events-none absolute -inset-4 -z-[5] h-[calc(100%+2rem)] w-[calc(100%+2rem)] text-destructive/40"
          >
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-dasharray="3 8"
              stroke-linecap="round"
              class="origin-center animate-[spin_24s_linear_infinite] opacity-50"
            />
            <circle cx="60" cy="6" r="3" fill="currentColor" class="opacity-70" />
            <circle cx="108" cy="78" r="2" fill="currentColor" class="opacity-50" />
            <circle cx="14" cy="84" r="2.5" fill="currentColor" class="opacity-40" />
          </svg>
          <div class="rounded-full bg-gradient-to-br from-destructive/15 to-destructive/5 p-5 ring-1 ring-destructive/20">
            <AlertTriangle class="h-10 w-10 text-destructive/80" />
          </div>
        </div>

        <div class="space-y-2">
          <h2 class="text-2xl font-bold tracking-tight">{t(msg`Something went wrong`)}</h2>
          <p class="text-balance text-sm text-muted-foreground">
            {props.inline
              ? t(msg`This section couldn't be displayed. Try again.`)
              : t(msg`Don't worry — your data is safe on this device.`)}
          </p>
          <p class="break-words text-xs text-muted-foreground/70">{message()}</p>
        </div>

        <div class="flex w-full gap-3">
          <Button
            variant="outline"
            class="flex-1 gap-2"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <RotateCcw class="h-4 w-4" />
            {t(msg`Reload`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: JSX.Element;
  /** Compact fallback for data regions instead of full-page. */
  inline?: boolean;
  /** Identifies the boundary in logs. */
  label?: string;
}

/**
 * Reusable Solid `<ErrorBoundary>` wrapper with the premium {@link ErrorFallback}.
 * Use globally at the app root and around data regions so a render error never
 * leaves a blank screen. The fallback's "Retry" calls Solid's `reset()` to
 * re-attempt rendering the subtree.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function AppErrorBoundary(props: AppErrorBoundaryProps) {
  return (
    <ErrorBoundary
      fallback={(error: unknown, reset: () => void) => {
        console.error(
          `[AppErrorBoundary${props.label ? `:${props.label}` : ""}] render error:`,
          error,
        );
        return <ErrorFallback error={error} reset={reset} inline={props.inline} />;
      }}
    >
      {props.children}
    </ErrorBoundary>
  );
}
